import React from 'react'
import { BsEye, BsPaperclip } from 'react-icons/bs'
import '../Styles/AdminPage.css'
import dayjs from 'dayjs'
import { Button, Modal, Spinner } from 'react-bootstrap'
import { FetchContext } from '../context/FetchContext'
import AdminPagination from '../components/AdminPagination'

const linky = process.env.REACT_APP_LINKY
const PAGE_LIMIT = 20

const formatDate = (value) => dayjs(value).format('DD.MM.YYYY HH:mm')
const getFileName = (file) => file?.originalFilename || file?.filename || file?.newFilename || 'Файл'

function FilesModal({
    files,
    label = 'Файлы',
    title = 'Файлы заявки',
    endpoint = 'api/download-request-file',
}) {
    const fetchContext = React.useContext(FetchContext)
    const [show, setShow] = React.useState(false)

    const downloadFile = async (file) => {
        // имя на диске (уникальное) — для запроса, оригинальное — для сохранения
        const storedName = file?.filename || file?.newFilename || getFileName(file)
        const displayName = getFileName(file)
        try {
            const { data } = await fetchContext.authAxios.get(
                `${endpoint}/${encodeURIComponent(storedName)}`,
                { responseType: 'arraybuffer' }
            )
            const blob = new Blob([data])
            const downloadUrl = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = downloadUrl
            link.download = displayName
            document.body.appendChild(link)
            link.click()
            link.remove()
            setTimeout(() => URL.revokeObjectURL(downloadUrl), 7000)
        } catch (err) {
            console.log(err)
        }
    }

    if (!files?.length) return null

    return (
        <>
            <button type="button" className="adminRequestFilesBtn" onClick={() => setShow(true)}>
                <BsPaperclip />
                <span>{label}: {files.length}</span>
            </button>

            <Modal show={show} onHide={() => setShow(false)} centered dialogClassName="adminFilesModal">
                <Modal.Header closeButton>
                    <Modal.Title>{title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="adminRequestFilesList">
                        {files.map((file, index) => {
                            const filename = getFileName(file)
                            return (
                                <button type="button" key={`${filename}-${index}`} onClick={() => downloadFile(file)}>
                                    <span>{filename}</span>
                                    <strong>Скачать</strong>
                                </button>
                            )
                        })}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShow(false)}>Закрыть</Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}

function RequestMessages({ messages }) {
    if (!messages?.length) return null

    return (
        <div className="adminNotesThread adminNotesThread--resolved">
            {messages.map((message) => (
                <div
                    key={message.MessageId}
                    className={`adminNotesMessage adminNotesMessage--${message.Sender === 'admin' ? 'admin' : 'user'}`}
                >
                    <div className="adminNotesMessage__meta">
                        <strong>{message.Sender === 'admin' ? 'Администратор' : 'Должник'}</strong>
                        <time>{formatDate(message.createdAt)}</time>
                    </div>
                    {message.Message && <p>{message.Message}</p>}
                    <FilesModal
                        files={message.File}
                        label="Вложения"
                        title={message.Sender === 'admin' ? 'Файлы ответа' : 'Файлы должника'}
                        endpoint={message.Sender === 'admin' ? 'api/download-notes' : 'api/download-request-file'}
                    />
                </div>
            ))}
        </div>
    )
}

function ResolvedRequestCard({ item, onOpenChat }) {
    const isAccountDocs = item.IsAccountDocs
    const messageCount = item.Messages?.length || 0

    return (
        <article className={`adminRequestCard adminRequestCard--resolved${isAccountDocs ? ' adminRequestCard--accountResponse' : ''}`}>
            <header className="adminRequestCard__header">
                <div>
                    <span className="adminRequestCard__id">Запрос №{item.ReqId}</span>
                    <span className={`adminRequestCard__source${isAccountDocs ? ' adminRequestCard__source--account' : ''}`}>
                        {isAccountDocs ? 'Закрытый чат личного кабинета' : 'Обычное обращение'}
                    </span>
                    <h3>{item.Name || 'Клиент не указан'}</h3>
                </div>
                <time>{formatDate(item.LastMessageAt || item.updatedAt || item.createdAt)}</time>
            </header>

            <div className="adminRequestCard__meta">
                <div className="adminRequestField adminRequestField--topic">
                    <span>Тема</span>
                    <strong>{item.Type || 'Без типа'}</strong>
                </div>
                {item.Email && (
                    <div className="adminRequestField">
                        <span>Email</span>
                        <strong>{item.Email}</strong>
                    </div>
                )}
                {item.DocNumber && (
                    <div className="adminRequestField">
                        <span>Договор</span>
                        <strong>{item.DocNumber}</strong>
                    </div>
                )}
            </div>

            {isAccountDocs ? (
                <div className="adminRequestCard__chatSummary">
                    <div>
                        <span>Переписка личного кабинета</span>
                        <strong>{messageCount} сообщ.</strong>
                    </div>
                    <button type="button" onClick={() => onOpenChat(item)}>
                        Открыть чат
                    </button>
                </div>
            ) : (
                <>
                    <div className="adminRequestCard__question">
                        <span>Текст обращения</span>
                        <p>{item.Question || 'Текст не указан'}</p>
                    </div>

                    {item.Comment && (
                        <div className="adminRequestCard__comment">
                            <span>Комментарий</span>
                            <p>{item.Comment}</p>
                        </div>
                    )}

                    {(item.ResponseFile || item.ResponseComment) && (
                        <div className="adminRequestCard__comment adminRequestCard__comment--response">
                            <span>Ответ пользователю</span>
                            {item.ResponseComment && <p>{item.ResponseComment}</p>}
                            {item.ResponseFile && (
                                <FilesModal
                                    files={[item.ResponseFile]}
                                    label="Отправленный файл"
                                    title="Файл, отправленный пользователю"
                                    endpoint="api/download-notes"
                                />
                            )}
                        </div>
                    )}
                </>
            )}

            <footer className="adminRequestCard__footer adminRequestCard__footer--resolved">
                <FilesModal files={item.File} label="Вложения заявки" title="Вложения заявки" />
            </footer>
        </article>
    )
}

function AdminPageResolved() {
    const fetchContext = React.useContext(FetchContext)
    const [requests, setRequests] = React.useState([])
    const [sorted, setSorted] = React.useState({ reversed: false })
    const [searchPhrase, setSearchPhrase] = React.useState('')
    const [debouncedSearch, setDebouncedSearch] = React.useState('')
    const [page, setPage] = React.useState(1)
    const [pagination, setPagination] = React.useState({
        page: 1,
        limit: PAGE_LIMIT,
        total: 0,
        totalPages: 1,
    })
    const [dataLoading, setDataLoading] = React.useState(false)
    const [channelFilter, setChannelFilter] = React.useState('all')
    const [activeChat, setActiveChat] = React.useState(null)

    const getDashboardData = React.useCallback(async () => {
        try {
            setDataLoading(true)
            const { data } = await fetchContext.authAxios.get('api/admin-reqs-done', {
                timeout: 12000,
                params: {
                    page,
                    limit: PAGE_LIMIT,
                    search: debouncedSearch,
                    sort: sorted.reversed ? 'old' : 'new',
                    channel: channelFilter,
                },
            })

            if (Array.isArray(data)) {
                setRequests(data)
                setPagination({
                    page: 1,
                    limit: data.length || PAGE_LIMIT,
                    total: data.length,
                    totalPages: 1,
                })
                return
            }

            setRequests(data.rows || [])
            setPagination({
                page: data.page || page,
                limit: data.limit || PAGE_LIMIT,
                total: data.total || 0,
                totalPages: data.totalPages || 1,
            })
        } catch (error) {
            console.log(error)
        } finally {
            setDataLoading(false)
        }
    }, [channelFilter, debouncedSearch, fetchContext, page, sorted.reversed])

    React.useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(searchPhrase.trim()), 350)
        return () => clearTimeout(timer)
    }, [searchPhrase])

    React.useEffect(() => {
        getDashboardData()
    }, [getDashboardData])

    const handleSearch = (event) => {
        setSearchPhrase(event.target.value)
        setPage(1)
    }

    const handleChannelFilter = (event) => {
        setChannelFilter(event.target.value)
        setPage(1)
    }

    const sortByDate = (reversed) => {
        setSorted({ reversed })
        setPage(1)
    }

    return (
        <React.Suspense>
            <div className="AdminContainer adminRequestsPage">
                <div className="adminRequestsToolbar">
                    <div>
                        <h1>Отработанные заявки</h1>
                        <p>Закрытые обращения и закрытые чаты личного кабинета.</p>
                    </div>
                    <div className="adminRequestsToolbar__controls">
                        <input
                            type="search"
                            placeholder="Поиск по заявке, ФИО, email, договору"
                            value={searchPhrase}
                            onChange={handleSearch}
                        />
                        <select value={channelFilter} onChange={handleChannelFilter}>
                            <option value="all">Все закрытые</option>
                            <option value="account">Чаты ЛК</option>
                            <option value="site">Обычные обращения</option>
                        </select>
                        <select
                            value={sorted.reversed ? 'old' : 'new'}
                            onChange={(event) => sortByDate(event.target.value === 'old')}
                        >
                            <option value="new">Сначала новые</option>
                            <option value="old">Сначала старые</option>
                        </select>
                        {linky && (
                            <a href={linky} target="__blank" className="adminRequestsToolbar__link" title="Открыть внешний ресурс">
                                <BsEye />
                            </a>
                        )}
                    </div>
                </div>

                <div className="adminRequestsSummary">
                    <span>Найдено: {requests.length}</span>
                    <span>Всего: {pagination.total}</span>
                </div>

                <AdminPagination
                    page={pagination.page}
                    totalPages={pagination.totalPages}
                    total={pagination.total}
                    limit={pagination.limit}
                    onPageChange={setPage}
                />

                <div className="AdminFeed adminRequestsList">
                    {dataLoading ? (
                        <div className="adminRequestsEmpty">
                            <Spinner animation="border" variant="success" />
                        </div>
                    ) : requests.length ? (
                        requests.map((item) => (
                            <ResolvedRequestCard key={item.ReqId} item={item} onOpenChat={setActiveChat} />
                        ))
                    ) : (
                        <div className="adminRequestsEmpty">Заявки не найдены</div>
                    )}
                </div>

                <Modal
                    show={Boolean(activeChat)}
                    onHide={() => setActiveChat(null)}
                    centered
                    dialogClassName="adminChatModal"
                >
                    {activeChat && (
                        <>
                            <Modal.Header closeButton>
                                <div className="adminChatModal__title">
                                    <span>Запрос №{activeChat.ReqId}</span>
                                    <h3>{activeChat.Type || 'Чат личного кабинета'}</h3>
                                    {activeChat.DocNumber && <p>Договор: {activeChat.DocNumber}</p>}
                                </div>
                            </Modal.Header>
                            <Modal.Body>
                                <RequestMessages messages={activeChat.Messages} />
                            </Modal.Body>
                        </>
                    )}
                </Modal>
            </div>
        </React.Suspense>
    )
}

export default React.memo(AdminPageResolved)
